import { type FC, useState, useCallback, useContext, useTransition } from 'react';
import classnames from 'classnames';
import { usePaginationFragment, graphql, useLazyLoadQuery } from 'react-relay';
import { FormattedMessage, defineMessages, useIntl } from 'dibs-react-intl';
import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback.js';

import { Input } from 'dibs-elements/exports/Input.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import Close from 'dibs-icons/exports/legacy/Close.ts';
import MagnifyingGlass from 'dibs-icons/exports/legacy/MagnifyingGlass.ts';
import { GetHelpZendeskSellerItemsListItem } from './GetHelpZendeskSellerItemsListItem.tsx';
import { GetHelpZendeskEmptyState } from './GetHelpZendeskEmptyState.tsx';

import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';

import { type GetHelpZendeskSellerItemsList_viewer$key } from './__generated__/GetHelpZendeskSellerItemsList_viewer.graphql.ts';
import { type GetHelpZendeskSellerItemsListQuery } from './__generated__/GetHelpZendeskSellerItemsListQuery.graphql.ts';
import { type GetHelpZendeskSellerItemsListPaginationQuery } from './__generated__/GetHelpZendeskSellerItemsListPaginationQuery.graphql.ts';

import dibsCss from 'dibs-css';

const messages = defineMessages({
    placeholder: {
        id: 'getHelp.GetHelpZendeskSellerItemsList.placeholder',
        defaultMessage: 'Search by Seller, Item Ref or Title',
    },
});

const MIN_SEARCH_LENGTH = 3;

const LoadingSpinner: FC = () => (
    <div className={classnames(dibsCss.flex, dibsCss.itemsCenter)}>
        <Spinner />
    </div>
);

const GetHelpZendeskSellerItemsList: FC = () => {
    const intl = useIntl();
    const {
        state: { sellerId },
    } = useContext(GetHelpZendeskContext);

    const [searchTerm, setSearchTerm] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const [isRefetching, startTransition] = useTransition();

    const { viewer } = useLazyLoadQuery<GetHelpZendeskSellerItemsListQuery>(
        graphql`
            query GetHelpZendeskSellerItemsListQuery(
                $searchTerm: String!
                $hasSellerId: Boolean!
                $dealerPk: [String]
            ) {
                viewer {
                    ...GetHelpZendeskSellerItemsList_viewer @include(if: $hasSellerId)
                }
            }
        `,
        {
            hasSellerId: !!sellerId,
            searchTerm: '',
            dealerPk: [sellerId],
        }
    );

    const { data, hasNext, loadNext, isLoadingNext, refetch } = usePaginationFragment<
        GetHelpZendeskSellerItemsListPaginationQuery,
        GetHelpZendeskSellerItemsList_viewer$key
    >(
        graphql`
            fragment GetHelpZendeskSellerItemsList_viewer on Viewer
            @refetchable(queryName: "GetHelpZendeskSellerItemsListPaginationQuery")
            @argumentDefinitions(
                after: { type: String, defaultValue: "" }
                first: { type: Int, defaultValue: 10 }
            ) {
                items(
                    dealerPk: $dealerPk
                    first: $first
                    after: $after
                    searchTerm: $searchTerm
                    queryTemplate: "dealer.inventory.management"
                    sort: "-createDate"
                    includeContextAdmin: true
                ) @connection(key: "GetHelpZendeskSellerItemsList_items") {
                    edges {
                        node {
                            serviceId
                            ...GetHelpZendeskSellerItemsListItem_item
                        }
                    }
                }
            }
        `,
        viewer || null
    );

    const fetchData = useCallback(
        (val: string) => {
            startTransition(() => {
                setHasSearched(true);
                refetch({ searchTerm: val });
            });
        },
        [refetch, startTransition]
    );

    const [debouncedFetchData] = useDebouncedCallback(fetchData, 500);

    const onChange = (searchTermValue: string): void => {
        setSearchTerm(searchTermValue);
        if (searchTermValue.length >= MIN_SEARCH_LENGTH || !searchTermValue) {
            debouncedFetchData(searchTermValue);
        }
    };

    const items = data?.items?.edges || [];
    const noItemsFound = !items.length && searchTerm;
    const hasItems = !!items.length;
    const noRecentItems = !hasItems && !hasSearched;

    return (
        <div className={dibsCss.mbLarge}>
            {(hasItems || hasSearched) && (
                <div className={dibsCss.mbMedium}>
                    <Input
                        dataTn="getHelp-items-list-input"
                        size="medium"
                        value={searchTerm}
                        onChange={e => onChange(e.target.value)}
                        placeholder={intl.formatMessage(messages.placeholder)}
                        rightDecorator={
                            <>
                                {searchTerm && (
                                    <Link onClick={() => onChange('')}>
                                        <Close />
                                    </Link>
                                )}
                            </>
                        }
                        leftDecorator={<MagnifyingGlass />}
                    />
                </div>
            )}
            {isRefetching ? (
                <LoadingSpinner />
            ) : (
                <>
                    {noRecentItems && (
                        <GetHelpZendeskEmptyState
                            title={
                                <FormattedMessage
                                    id="getHelp.GetHelpZendeskSellerItemsList.noListingsTitle"
                                    defaultMessage="No Listings Found"
                                />
                            }
                            subtitle={
                                <FormattedMessage
                                    id="getHelp.GetHelpZendeskSellerItemsList.noOrdersSubtitle"
                                    defaultMessage="You currently have no active listings. Once you create a listing, it will appear here. <createListingLine>Ready to list your first item? <createListingLink>Create a listing</createListingLink></createListingLine>"
                                    values={{
                                        createListingLine: content => (
                                            <span
                                                className={classnames(
                                                    dibsCss.mtSmall,
                                                    dibsCss.block
                                                )}
                                            >
                                                {content}
                                            </span>
                                        ),
                                        createListingLink: content => (
                                            <Link href={`/dealers/item/`} target="_blank">
                                                <span className={dibsCss.sassyFontBody}>
                                                    {content}
                                                </span>
                                            </Link>
                                        ),
                                    }}
                                />
                            }
                        />
                    )}
                    <div className={classnames(dibsCss.flex, dibsCss.flexCol, dibsCss.gapSmall)}>
                        {items.map(item => (
                            <GetHelpZendeskSellerItemsListItem
                                key={item?.node?.serviceId || ''}
                                item={item?.node || null}
                            />
                        ))}
                        {isLoadingNext && hasNext && (
                            <div
                                className={classnames(
                                    dibsCss.wFull,
                                    dibsCss.flex,
                                    dibsCss.justifyCenter
                                )}
                            >
                                <LoadingSpinner />
                            </div>
                        )}
                        {hasNext && !isLoadingNext && (
                            <Link
                                dataTn="get-help-modal-show-more-items-cta"
                                onClick={() => loadNext(10)}
                            >
                                <span
                                    className={classnames(dibsCss.textLeft, dibsCss.sassyFontBody)}
                                >
                                    <FormattedMessage
                                        id="getHelp.GetHelpZendeskSellerItemsList.loadMore"
                                        defaultMessage="Show More Listings"
                                    />
                                </span>
                            </Link>
                        )}
                        {noItemsFound && (
                            <FormattedMessage
                                id="getHelp.GetHelpZendeskSellerItemsList.noResultsFound"
                                defaultMessage="We couldn’t find any results for “{searchTerm}”"
                                values={{ searchTerm }}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export { GetHelpZendeskSellerItemsList };
